import React, { useEffect, useRef } from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import { Avatar, MenuItem, Select } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import ChatIcon from '@mui/icons-material/Chat';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import { Skeleton } from '@material-ui/lab';
import { useNotifications } from 'app/store/contexts/NotificationsContext';

// Channel → small badge icon shown over the avatar + label color.
const CHANNEL_META = {
	whatsapp:       { Icon: WhatsAppIcon,  color: '#25D366', label: 'WhatsApp' },
	waba_web:       { Icon: WhatsAppIcon,  color: '#25D366', label: 'Web Chat' },
	facebook:       { Icon: FacebookIcon,  color: '#1877F2', label: 'Facebook' },
	facebook_post:  { Icon: FacebookIcon,  color: '#1877F2', label: 'Facebook' },
	messenger:      { Icon: ChatIcon,      color: '#0084FF', label: 'Messenger' },
	instagram:      { Icon: InstagramIcon, color: '#E1306C', label: 'Instagram' },
	email:          { Icon: EmailIcon,     color: '#EA4335', label: 'Email' },
	sms:            { Icon: SmsIcon,       color: '#9CA3AF', label: 'SMS' }
};

const useStyles = makeStyles(theme => ({
	'@global': {
		'@keyframes notifSlideIn': {
			from: { opacity: 0, transform: 'translateX(20px)' },
			to:   { opacity: 1, transform: 'translateX(0)' }
		},
		'@keyframes notifDotPulse': {
			'0%':   { boxShadow: '0 0 0 0 rgba(239,68,68,0.55)' },
			'70%':  { boxShadow: '0 0 0 6px rgba(239,68,68,0)' },
			'100%': { boxShadow: '0 0 0 0 rgba(239,68,68,0)' }
		}
	},
	paper: {
		width: 420,
		maxWidth: '95vw',
		background: theme.palette.background.paper,
		display: 'flex',
		flexDirection: 'column',
		borderLeft: 'none',
		// Rounded left edge softens the drawer — no more sharp corner against
		// the page. Right edge stays flush so it still feels like it's
		// docked to the viewport.
		borderTopLeftRadius: 24,
		borderBottomLeftRadius: 24,
		boxShadow: theme.palette.type === 'dark'
			? '-16px 0 48px rgba(0,0,0,0.55), -2px 0 0 rgba(255,255,255,0.04)'
			: '-16px 0 48px rgba(0,0,0,0.12), -2px 0 0 rgba(0,0,0,0.02)',
		overflow: 'hidden'
	},
	header: {
		padding: theme.spacing(2.5, 2.5, 1.5),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		// Subtle tinted gradient strip behind the title so the header has
		// its own visual band instead of bleeding into the list.
		background: theme.palette.type === 'dark'
			? 'linear-gradient(180deg, rgba(24,119,242,0.10) 0%, rgba(24,119,242,0.00) 100%)'
			: 'linear-gradient(180deg, rgba(24,119,242,0.08) 0%, rgba(24,119,242,0.00) 100%)',
		borderBottom: theme.palette.type === 'dark'
			? '1px solid rgba(255,255,255,0.06)'
			: '1px solid rgba(0,0,0,0.05)'
	},
	title: {
		fontSize: '1.5rem',
		fontWeight: 700,
		fontFamily: theme.typography?.fontFamily,
		color: theme.palette.text.primary,
		letterSpacing: '-0.01em',
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1)
	},
	titleDot: {
		width: 8,
		height: 8,
		borderRadius: '50%',
		background: '#1877F2',
		boxShadow: '0 0 0 4px rgba(24,119,242,0.18)'
	},
	headerActions: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(0.75)
	},
	closeBtn: {
		width: 34,
		height: 34,
		background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
		color: theme.palette.text.secondary,
		transition: 'background 0.18s ease, transform 0.18s ease, color 0.18s ease',
		'&:hover': {
			background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.06)',
			color: theme.palette.text.primary,
			transform: 'rotate(90deg)'
		}
	},
	markAllBtn: {
		fontSize: 12,
		fontWeight: 600,
		background: theme.palette.type === 'dark' ? 'rgba(24,119,242,0.14)' : 'rgba(24,119,242,0.10)',
		border: 'none',
		color: '#1877F2',
		cursor: 'pointer',
		padding: '7px 12px',
		borderRadius: 999,
		transition: 'background 0.18s ease, color 0.18s ease, opacity 0.18s ease, transform 0.18s ease',
		'&:hover': {
			background: theme.palette.type === 'dark' ? 'rgba(24,119,242,0.24)' : 'rgba(24,119,242,0.16)'
		},
		'&:disabled': { opacity: 0.4, cursor: 'not-allowed', background: 'transparent' }
	},
	// --- tabs + filter row ---
	subHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: theme.spacing(1.5, 2.5, 1.5),
		gap: theme.spacing(1)
	},
	tabs: {
		display: 'flex',
		gap: theme.spacing(0.5),
		background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.045)',
		padding: 5,
		borderRadius: 999
	},
	tab: {
		fontSize: 12,
		fontWeight: 600,
		padding: '7px 14px',
		borderRadius: 999,
		border: 'none',
		background: 'transparent',
		color: theme.palette.text.secondary,
		cursor: 'pointer',
		transition: 'background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		'&:hover': { color: theme.palette.text.primary }
	},
	tabActive: {
		background: theme.palette.background.paper,
		color: theme.palette.text.primary,
		boxShadow: theme.palette.type === 'dark'
			? '0 2px 8px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04)'
			: '0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.03)'
	},
	tabBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		minWidth: 18,
		height: 18,
		padding: '0 5px',
		borderRadius: 999,
		fontSize: 10,
		fontWeight: 700,
		background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
		color: '#fff',
		marginLeft: 2,
		boxShadow: '0 2px 6px rgba(239,68,68,0.35)'
	},
	channelSelect: {
		fontSize: 12,
		minWidth: 140,
		'& .MuiOutlinedInput-root': {
			borderRadius: '999px !important',
			background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
			transition: 'background 0.18s ease'
		},
		'& .MuiOutlinedInput-notchedOutline': {
			borderRadius: '999px !important',
			borderColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
		},
		'& .MuiOutlinedInput-root:hover': {
			background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'
		},
		'& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
			borderColor: 'rgba(24,119,242,0.45)'
		},
		'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
			borderColor: '#1877F2',
			borderWidth: 1
		},
		'& .MuiSelect-select': {
			padding: '7px 32px 7px 16px',
			borderRadius: 999
		},
		'& .MuiSelect-select:focus': {
			background: 'transparent',
			borderRadius: 999
		}
	},
	// --- body ---
	body: {
		flex: 1,
		overflowY: 'auto',
		padding: theme.spacing(0.5, 1.5, 2),
		'&::-webkit-scrollbar': { width: 6 },
		'&::-webkit-scrollbar-track': { background: 'transparent' },
		'&::-webkit-scrollbar-thumb': {
			background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)',
			borderRadius: 999,
			'&:hover': {
				background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.20)'
			}
		}
	},
	// Sentinel row at the bottom of the list that shows a spinner while the
	// next page is being fetched and an "all caught up" hint when the user
	// has scrolled through everything.
	loadMoreSentinel: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: theme.spacing(2),
		minHeight: 48,
		color: theme.palette.text.secondary,
		fontSize: 12
	},
	// Skeleton tile: mirrors the real row layout (avatar + title + desc +
	// meta) so the transition to real content is seamless.
	skelRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.25, 1.5),
		marginBottom: theme.spacing(1),
		borderRadius: 12,
		'&:last-child': { marginBottom: 0 }
	},
	skelBody: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 6
	},
	emptyState: {
		padding: theme.spacing(8, 3),
		textAlign: 'center',
		color: theme.palette.text.secondary,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		animation: '$notifSlideIn 0.3s ease both'
	},
	emptyIconWrap: {
		width: 64,
		height: 64,
		borderRadius: '50%',
		background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: theme.spacing(0.5)
	},
	emptyIcon: { fontSize: 32, color: theme.palette.text.secondary },
	emptyTitle: { fontSize: 15, fontWeight: 700, color: theme.palette.text.primary },
	emptySubtitle: { fontSize: 12, maxWidth: 260, lineHeight: 1.45, color: theme.palette.text.secondary },
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1.5),
		padding: theme.spacing(1.5, 1.75),
		marginBottom: theme.spacing(0.75),
		borderRadius: 16,
		cursor: 'pointer',
		position: 'relative',
		transition: 'background 0.22s ease, box-shadow 0.22s ease',
		animation: '$notifSlideIn 0.28s cubic-bezier(0.22, 0.61, 0.36, 1) both',
		'&:hover': {
			background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
			boxShadow: theme.palette.type === 'dark'
				? '0 2px 12px rgba(0,0,0,0.35)'
				: '0 2px 12px rgba(0,0,0,0.06)'
		},
		'&:last-child': { marginBottom: 0 }
	},
	rowUnread: {
		background: theme.palette.type === 'dark' ? 'rgba(24,119,242,0.10)' : 'rgba(24,119,242,0.07)',
		// Soft blue accent stripe on the left, marking unread rows.
		'&::before': {
			content: '""',
			position: 'absolute',
			left: 6,
			top: '50%',
			transform: 'translateY(-50%)',
			width: 3,
			height: '60%',
			borderRadius: 999,
			background: '#1877F2',
			boxShadow: '0 0 8px rgba(24,119,242,0.6)'
		}
	},
	avatarWrap: { position: 'relative', flexShrink: 0 },
	avatar: {
		width: 48,
		height: 48,
		background: theme.palette.type === 'dark' ? '#374151' : '#E4E6EB',
		color: theme.palette.text.primary,
		fontWeight: 700,
		fontSize: 16
	},
	channelBadge: {
		position: 'absolute',
		right: -2,
		bottom: -2,
		width: 22,
		height: 22,
		borderRadius: '50%',
		background: theme.palette.background.paper,
		border: `2px solid ${theme.palette.background.paper}`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		boxShadow: theme.palette.type === 'dark'
			? '0 1px 4px rgba(0,0,0,0.5)'
			: '0 1px 4px rgba(0,0,0,0.18)'
	},
	rowBody: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
	rowTitle: {
		fontSize: 14,
		fontWeight: 700,
		color: theme.palette.text.primary,
		lineHeight: 1.35
	},
	rowDesc: {
		fontSize: 13,
		color: theme.palette.text.secondary,
		lineHeight: 1.4,
		wordBreak: 'break-word',
		display: '-webkit-box',
		WebkitLineClamp: 2,
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden'
	},
	rowMeta: {
		fontSize: 12,
		color: theme.palette.text.secondary,
		fontWeight: 600,
		marginTop: 2,
		display: 'flex',
		alignItems: 'center',
		gap: 6
	},
	unreadDot: {
		alignSelf: 'center',
		flexShrink: 0,
		width: 10,
		height: 10,
		borderRadius: '50%',
		background: '#EF4444',
		marginLeft: theme.spacing(0.5),
		animation: '$notifDotPulse 1.6s ease-out infinite'
	}
}));

const getInitials = name => {
	if (!name) return '•';
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(p => p[0]?.toUpperCase())
		.join('');
};

const formatRelativeTime = (timestamp) => {
	if (!timestamp) return '';
	const ms = typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime();
	if (Number.isNaN(ms)) return '';
	const seconds = Math.max(0, Math.floor((Date.now() - ms) / 1000));
	if (seconds < 60) return `${seconds || 1}s`;
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d`;
	const weeks = Math.floor(days / 7);
	if (weeks < 5) return `${weeks}w`;
	const months = Math.floor(days / 30);
	if (months < 12) return `${months}mo`;
	const years = Math.floor(days / 365);
	return `${years}y`;
};

// Rough height of one notification row including its bottom margin. Used to
// pick an initial `limit` that exactly fills the visible body the first time
// the drawer opens.
const ROW_HEIGHT_PX = 88;

function NotificationsSidebar() {
	const classes = useStyles();
	const {
		open,
		closeSidebar,
		notifications,
		loading,
		unreadCount,
		activeTab,
		setActiveTab,
		activeChannelId,
		setActiveChannelId,
		channelOptions,
		activateNotification,
		markAllAsRead,
		setLimit,
		loadingMore,
		hasMoreNotifications,
		loadMoreNotifications
	} = useNotifications();

	const bodyRef = useRef(null);

	// Measure the body once the drawer opens and pick a limit that fills it
	// (plus a couple of rows of buffer). Re-measure if the viewport changes.
	useEffect(() => {
		if (!open) return undefined;
		const measure = () => {
			const el = bodyRef.current;
			if (!el) return;
			const visibleRows = Math.ceil(el.clientHeight / ROW_HEIGHT_PX) + 2;
			const next = Math.max(5, visibleRows); // never ask for fewer than 5
			setLimit(prev => (prev === next ? prev : next));
		};
		// Defer one frame so the drawer has actually laid out.
		const t = setTimeout(measure, 0);
		window.addEventListener('resize', measure);
		return () => {
			clearTimeout(t);
			window.removeEventListener('resize', measure);
		};
	}, [open, setLimit]);

	// Infinite scroll — trigger loadMore when the user gets within ~120px of
	// the bottom of the scroll container.
	const handleScroll = e => {
		const el = e.currentTarget;
		const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
		if (distanceFromBottom < 120 && hasMoreNotifications && !loadingMore && !loading) {
			loadMoreNotifications();
		}
	};

	// Single shared activate path — same offline gate, mark-as-read, and
	// inbox navigation that the live socket toast uses.
	const handleRowClick = (event, n) => {
		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}
		activateNotification(n);
	};

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={closeSidebar}
			classes={{ paper: classes.paper }}
			ModalProps={{ keepMounted: true }}
			transitionDuration={{ enter: 320, exit: 240 }}
			SlideProps={{
				easing: {
					enter: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
					exit: 'cubic-bezier(0.4, 0, 1, 1)'
				}
			}}
		>
			<div className={classes.header}>
				<div className={classes.title}>
					<span className={classes.titleDot} aria-hidden />
					Notifications
				</div>
				<div className={classes.headerActions}>
					<button
						type="button"
						className={classes.markAllBtn}
						onClick={markAllAsRead}
						disabled={loading || unreadCount === 0}
					>
						Mark all as read
					</button>
					<IconButton size="small" className={classes.closeBtn} onClick={closeSidebar}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</div>
			</div>

			<div className={classes.subHeader}>
				<div className={classes.tabs}>
					<button
						type="button"
						className={`${classes.tab} ${activeTab === 'all' ? classes.tabActive : ''}`}
						onClick={() => setActiveTab('all')}
					>
						All
					</button>
					<button
						type="button"
						className={`${classes.tab} ${activeTab === 'unread' ? classes.tabActive : ''}`}
						onClick={() => setActiveTab('unread')}
					>
						Unread
						{unreadCount > 0 && (
							<span className={classes.tabBadge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
						)}
					</button>
				</div>

				<Select
					value={activeChannelId}
					onChange={e => setActiveChannelId(e.target.value)}
					variant="outlined"
					className={classes.channelSelect}
					MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' } }}
				>
					{channelOptions.map(opt => (
						<MenuItem key={opt.id} value={opt.id} style={{ fontSize: 13 }}>
							{opt.label}
						</MenuItem>
					))}
				</Select>
			</div>

			<div className={classes.body} ref={bodyRef} onScroll={handleScroll}>
				{loading ? (
					// Skeleton placeholder tiles, sized identically to real
					// notification rows so the transition to loaded content
					// doesn't jump.
					[0, 1, 2, 3, 4].map(i => (
						<div key={`skel-${i}`} className={classes.skelRow}>
							<Skeleton variant="circle" width={48} height={48} />
							<div className={classes.skelBody}>
								<Skeleton variant="text" width={`${65 - (i % 3) * 8}%`} height={16} />
								<Skeleton variant="text" width={`${85 - (i % 2) * 10}%`} height={14} />
								<Skeleton variant="text" width="40%" height={12} />
							</div>
						</div>
					))
				) : notifications.length === 0 ? (
					<div className={classes.emptyState}>
						<div className={classes.emptyIconWrap}>
							<NotificationsNoneIcon className={classes.emptyIcon} />
						</div>
						<div className={classes.emptyTitle}>You're all caught up</div>
						<div className={classes.emptySubtitle}>
							{activeTab === 'unread'
								? 'No unread notifications for the selected channel.'
								: 'New activity across your channels will show up here.'}
						</div>
					</div>
				) : (
					notifications.map((n, i) => {
						const meta = CHANNEL_META[(n.channel_type || '').toLowerCase()] || null;
						const ChannelIcon = meta?.Icon || null;
						const channelColor = meta?.color || '#9CA3AF';
						const channelLabel = meta?.label || (n.channel_type || '').replace(/^./, c => c.toUpperCase());
						const displayTitle = n.title || n.actor_name || channelLabel || 'Notification';
						const isUnread = !n.is_read;
						return (
							<div
								key={n.id}
								className={`${classes.row} ${isUnread ? classes.rowUnread : ''}`}
								role="button"
								tabIndex={0}
								onClick={e => handleRowClick(e, n)}
								style={{ animationDelay: `${Math.min(i, 6) * 40}ms` }}
							>
								<div className={classes.avatarWrap}>
									<Avatar
										src={n.actor_profile_pic || undefined}
										className={classes.avatar}
									>
										{n.actor_profile_pic ? null : getInitials(n.actor_name || channelLabel)}
									</Avatar>
									{ChannelIcon && (
										<span className={classes.channelBadge}>
											<ChannelIcon style={{ fontSize: 14, color: channelColor }} />
										</span>
									)}
								</div>
								<div className={classes.rowBody}>
									<div className={classes.rowTitle}>{displayTitle}</div>
									{n.description ? (
										<div className={classes.rowDesc}>{n.description}</div>
									) : null}
									<div className={classes.rowMeta}>
										<span style={{ color: channelColor }}>{channelLabel}</span>
										<span>·</span>
										<span>{formatRelativeTime(n.dt)}</span>
									</div>
								</div>
								{isUnread && <span className={classes.unreadDot} aria-hidden />}
							</div>
						);
					})
				)}
				{/* Pagination skeletons — when loading the next page show a few
				    placeholder tiles below the existing list, matching the
				    real row layout so the list keeps growing smoothly. */}
				{!loading && loadingMore && (
					[0, 1, 2].map(i => (
						<div key={`skel-more-${i}`} className={classes.skelRow}>
							<Skeleton variant="circle" width={48} height={48} />
							<div className={classes.skelBody}>
								<Skeleton variant="text" width={`${65 - (i % 3) * 8}%`} height={16} />
								<Skeleton variant="text" width={`${85 - (i % 2) * 10}%`} height={14} />
								<Skeleton variant="text" width="40%" height={12} />
							</div>
						</div>
					))
				)}
				{/* End-of-list hint once everything has been loaded. */}
				{!loading && !loadingMore && notifications.length > 0 && !hasMoreNotifications && (
					<div className={classes.loadMoreSentinel}>
						<span>No more notifications</span>
					</div>
				)}
			</div>
		</Drawer>
	);
}

export default NotificationsSidebar;
